import { styled } from '@linaria/react'
import React, { FC, ReactNode } from 'react'
import { Typography } from './Typography'
import { colors } from '../styles/colors'

export type NavItemProps = {
  icon: ReactNode
  isActive?: boolean
  hasNotification?: boolean
  onClick?: () => void
  formattedMessage: ReactNode
}

export const NavItem: FC<NavItemProps> = ({
  icon,
  isActive = false,
  hasNotification = false,
  onClick,
  formattedMessage,
}) => {
  return (
    <NavItemContainer onClick={onClick} $isActive={isActive}>
      <IconContainer $isActive={isActive}>
        {icon}
        {hasNotification && <NotificationDot />}
      </IconContainer>

      <StyledTypography $isActive={isActive} fontSize={10} font="roboto">
        {formattedMessage}
      </StyledTypography>
    </NavItemContainer>
  )
}

const NavItemContainer = styled.button<{ $isActive: boolean }>`
  background: ${props =>
    props.$isActive ? colors.bgSecondary : 'transparent'};
  border: none;
  border-top: ${props =>
    props.$isActive ? `2px solid ${colors.grey800}` : '2px solid transparent'};
  padding: 16px 8px 8px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  flex: 1;
  transition: all 0.2s ease;
  position: relative;
  min-height: 56px;

  &:hover {
    background: ${props =>
      props.$isActive ? colors.secondaryLight : colors.bgTertiary};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 12px 6px 6px 6px;
    gap: 4px;
    min-height: 48px;
  }

  @media (max-width: 480px) {
    padding: 8px 4px 6px 4px;
    gap: 3px;
    min-height: 44px;
  }
`

const IconContainer = styled.div<{ $isActive: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: all 0.2s ease;
  color: ${colors.secondary};

  svg {
    width: 24px;
    height: 24px;
    transition: color 0.2s ease;
  }

  @media (max-width: 768px) {
    width: 22px;
    height: 22px;

    svg {
      width: 22px;
      height: 22px;
    }
  }

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`

const StyledTypography = styled(Typography)<{ $isActive: boolean }>`
  color: ${props =>
    props.$isActive ? colors.secondaryActive : colors.secondary};
  text-align: center;
  transition: all 0.2s ease;
  white-space: nowrap;
  line-height: 1.2;
  font-family: ${props =>
    props.$isActive ? 'Manrope, sans-serif' : 'Roboto, sans-serif'};

  @media (max-width: 768px) {
    font-size: 9px;
  }

  @media (max-width: 480px) {
    font-size: 8px;
  }
`

const NotificationDot = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background-color: ${colors.danger};
  border-radius: 50%;
  border: 2px solid ${colors.surfacePrimary};
  box-shadow: 0 1px 3px ${colors.shadowLight};

  @media (max-width: 768px) {
    width: 7px;
    height: 7px;
    border-width: 1.5px;
  }

  @media (max-width: 480px) {
    width: 6px;
    height: 6px;
    border-width: 1px;
  }
`
